<template>
  <div 
    class="action-bar" 
    :class="[
      `action-bar-${orientation}`,
      { 'action-bar-compact': compact }
    ]"
    role="toolbar"
    :aria-label="ariaLabel"
    :aria-orientation="orientation"
  >
    <div
      v-for="(action, index) in visibleActions"
      :key="getActionKey(action, index)"
      class="action-item"
      :class="{
        'action-item-separator': action.type === 'separator',
        'action-item-dropdown': action.type === 'dropdown',
        'action-item-disabled': isActionDisabled(action),
        'action-item-active': action.type === 'button' && isActionActive(action),
        'action-item-primary': 'primary' in action && action.primary
      }"
    >
      <!-- Separator -->
      <div 
        v-if="action.type === 'separator'" 
        class="action-separator"
        role="separator"
      ></div>
      
      <!-- Regular Button Action -->
      <button
        v-else-if="!action.type || action.type === 'button'"
        class="action-button"
        :disabled="isActionDisabled(action)"
        :title="getActionTooltip(action)"
        :aria-label="action.ariaLabel || action.label"
        :aria-pressed="action.toggle ? isActionActive(action) : undefined"
        @click="executeAction(action)"
        @keydown.enter="executeAction(action)"
        @keydown.space.prevent="executeAction(action)"
      >
        <Icon
          v-if="action.icon"
          :name="action.icon"
          :size="iconSize"
          class="action-icon"
        />
        <span
          v-if="action.label && showLabels"
          class="action-label"
        >
          {{ action.label }}
        </span>
      </button>
      
      <!-- Dropdown Action -->
      <div
        v-else-if="action.type === 'dropdown'"
        class="action-dropdown"
      >
        <button
          class="action-button action-dropdown-trigger"
          :disabled="isActionDisabled(action)"
          :title="getActionTooltip(action)"
          :aria-label="action.ariaLabel || action.label"
          :aria-expanded="openDropdowns.has(action.id)"
          :aria-haspopup="true"
          @click="toggleDropdown(action)"
          @keydown.enter="toggleDropdown(action)"
          @keydown.space.prevent="toggleDropdown(action)"
          @keydown.escape="closeDropdown(action.id)"
          @keydown.arrow-down.prevent="openDropdown(action)"
        >
          <Icon
            v-if="action.icon"
            :name="action.icon"
            :size="iconSize"
            class="action-icon"
          />
          <span
            v-if="action.label && showLabels"
            class="action-label"
          >
            {{ action.label }}
          </span>
          <Icon
            name="chevron-down"
            :size="12"
            class="action-dropdown-arrow"
            :class="{ 'action-dropdown-arrow-open': openDropdowns.has(action.id) }"
          />
        </button>
        
        <div
          v-if="openDropdowns.has(action.id)"
          class="action-dropdown-menu"
          :class="dropdownMenuClass"
          role="menu"
          :aria-label="action.menuAriaLabel"
        >
          <div
            v-for="(item, itemIndex) in action.items"
            :key="getActionKey(item, itemIndex)"
            class="action-dropdown-item"
            :class="{
              'action-dropdown-item-separator': item.type === 'separator',
              'action-dropdown-item-disabled': isActionDisabled(item)
            }"
          >
            <div
              v-if="item.type === 'separator'"
              class="action-dropdown-separator"
              role="separator"
            ></div>
            <button
              v-else
              class="action-dropdown-button"
              :disabled="isActionDisabled(item)"
              :title="getActionTooltip(item)"
              :aria-label="item.ariaLabel || item.label"
              role="menuitem"
              @click="executeAction(item); closeDropdown(action.id)"
            >
              <Icon
                v-if="item.icon"
                :name="item.icon"
                :size="16"
                class="action-dropdown-icon"
              />
              <span class="action-dropdown-label">{{ item.label }}</span>
              <span
                v-if="item.keybinding"
                class="action-dropdown-keybinding"
              >
                {{ item.keybinding }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import Icon from '../atoms/Icon.vue'

interface BaseAction {
  id: string
  label?: string
  icon?: string
  tooltip?: string
  ariaLabel?: string
  disabled?: boolean | (() => boolean)
  visible?: boolean | (() => boolean)
  keybinding?: string
  primary?: boolean
}

interface ButtonAction extends BaseAction {
  type?: 'button'
  toggle?: boolean
  active?: boolean | (() => boolean)
  action: () => void
}

interface DropdownAction extends BaseAction {
  type: 'dropdown'
  items: (ButtonAction | SeparatorAction)[]
  menuAriaLabel?: string
}

interface SeparatorAction {
  type: 'separator'
  id: string
  visible?: boolean | (() => boolean)
}

type Action = ButtonAction | DropdownAction | SeparatorAction

interface Props {
  actions: Action[]
  orientation?: 'horizontal' | 'vertical'
  compact?: boolean
  showLabels?: boolean
  iconSize?: number
  ariaLabel?: string
  dropdownMenuClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  orientation: 'horizontal',
  compact: false,
  showLabels: true,
  iconSize: 16,
  ariaLabel: 'Actions'
})

const emit = defineEmits<{
  'action': [actionId: string, action: Action]
}>()

const openDropdowns = ref<Set<string>>(new Set())

const visibleActions = computed(() => {
  return props.actions.filter(action => {
    if (action.visible === undefined) return true
    return typeof action.visible === 'function' ? action.visible() : action.visible
  })
})

const getActionKey = (action: Action, index: number): string => {
  return action.id || `action-${index}`
}

const isActionDisabled = (action: Action): boolean => {
  if (action.type === 'separator') return false
  if (action.disabled === undefined) return false
  return typeof action.disabled === 'function' ? action.disabled() : action.disabled
}

const isActionActive = (action: ButtonAction): boolean => {
  if (!action.toggle) return false
  if (action.active === undefined) return false
  return typeof action.active === 'function' ? action.active() : action.active
}

const getActionTooltip = (action: BaseAction): string => {
  const tooltip = action.tooltip || action.label || ''
  return action.keybinding ? `${tooltip} (${action.keybinding})` : tooltip
}

const executeAction = (action: ButtonAction) => {
  if (isActionDisabled(action)) return
  
  try {
    action.action()
    emit('action', action.id, action)
  } catch (error) {
    console.error('Error executing action:', error)
  }
}

const toggleDropdown = (action: DropdownAction) => {
  if (isActionDisabled(action)) return
  
  if (openDropdowns.value.has(action.id)) {
    closeDropdown(action.id)
  } else {
    openDropdown(action)
  }
}

const openDropdown = (action: DropdownAction) => {
  if (isActionDisabled(action)) return
  
  // Close other dropdowns
  openDropdowns.value.clear()
  openDropdowns.value.add(action.id)
}

const closeDropdown = (actionId: string) => {
  openDropdowns.value.delete(actionId)
}

const closeAllDropdowns = () => {
  openDropdowns.value.clear()
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.action-dropdown')) {
    closeAllDropdowns()
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeAllDropdowns()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped lang="scss">

.action-bar {
  display: flex;
  align-items: center;
  gap: 1px;
  background: var(--vscode-toolbar-background);
  border-radius: 2px;
  
  &.action-bar-vertical {
    flex-direction: column;
  }
  
  &.action-bar-compact {
    gap: 0;
  }
}

.action-item {
  position: relative;
  
  &.action-item-separator {
    margin: 0 4px;
  }
}

.action-separator {
  width: 1px;
  height: 16px;
  background: var(--vscode-toolbar-separator);
  
  .action-bar-vertical & {
    width: 16px;
    height: 1px;
  }
}

.action-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: var(--vscode-foreground);
  cursor: pointer;
  border-radius: 2px;
  font-size: 13px;
  line-height: 18px;
  min-height: 26px;
  
  &:hover:not(:disabled) {
    background: var(--vscode-toolbar-hoverBackground);
    color: var(--vscode-toolbar-hoverOutline);
  }
  
  &:focus {
    outline: none;
    background: var(--vscode-toolbar-activeBackground);
    box-shadow: 0 0 0 1px var(--vscode-focusBorder);
  }
  
  &:active:not(:disabled) {
    background: var(--vscode-toolbar-activeBackground);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  .action-item-active & {
    background: var(--vscode-toolbar-activeBackground);
    color: var(--vscode-toolbar-activeForeground);
  }
  
  .action-item-primary & {
    background: var(--vscode-button-background);
    color: var(--vscode-button-foreground);
    
    &:hover:not(:disabled) {
      background: var(--vscode-button-hoverBackground);
    }
  }
  
  .action-bar-compact & {
    padding: 2px 4px;
    min-height: 22px;
  }
}

.action-icon {
  flex-shrink: 0;
}

.action-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Dropdown specific styles
.action-dropdown {
  position: relative;
}

.action-dropdown-trigger {
  padding-right: 4px;
}

.action-dropdown-arrow {
  margin-left: 2px;
  transition: transform 0.15s ease;
  
  &.action-dropdown-arrow-open {
    transform: rotate(180deg);
  }
}

.action-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: var(--vscode-menu-background);
  border: 1px solid var(--vscode-menu-border);
  border-radius: 2px;
  box-shadow: 0 2px 8px var(--vscode-widget-shadow);
  min-width: 160px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
  
  .action-bar-vertical & {
    top: 0;
    left: 100%;
    margin-left: 4px;
  }
}

.action-dropdown-item {
  &.action-dropdown-item-separator {
    margin: 4px 0;
  }
}

.action-dropdown-separator {
  height: 1px;
  background: var(--vscode-menu-separatorBackground);
  margin: 0 8px;
}

.action-dropdown-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 12px;
  background: transparent;
  border: none;
  color: var(--vscode-menu-foreground);
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  
  &:hover:not(:disabled) {
    background: var(--vscode-menu-selectionBackground);
    color: var(--vscode-menu-selectionForeground);
  }
  
  &:focus {
    outline: none;
    background: var(--vscode-menu-selectionBackground);
    color: var(--vscode-menu-selectionForeground);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.action-dropdown-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.action-dropdown-label {
  flex: 1;
}

.action-dropdown-keybinding {
  margin-left: 16px;
  opacity: 0.7;
  font-size: 12px;
  flex-shrink: 0;
}
</style>
